import React, { Component } from 'react';
import { Form } from 'react-bootstrap';

import { autoGrow } from './autoGrow';

/**
 * GrowTextarea — the ONE multi-line text field this widget edits prose in.
 *
 * A plain Bootstrap textarea that sizes itself to its content up to `maxRows` and then scrolls
 * (`autoGrow`), keeping its own ref and re-measuring on every render — so a value seeded from
 * elsewhere, a switched selection or a mode toggle all land at the right height without the caller
 * threading refs through its lifecycle.
 *
 * It exists because the same field is edited in two places — the cell panel and the mission panel.
 * One field edited through two different controls is the drift this component removes: whatever is
 * true of one panel's textarea is true of the other's by construction.
 *
 * Props:
 *   value       — string
 *   onChange    — (value: string) => void   (the VALUE, not the event: every caller wants the value)
 *   onBlur      — () => void                (optional; the panels save on blur)
 *   rows        — the resting height before content is measured (default 2)
 *   maxRows     — lines to grow to before scrolling (TITLE_ROWS / DESCRIPTION_ROWS)
 *   className   — passed through, for the fields that sit inside a styled row (a question's
 *                 free-text option, the composer shell) rather than in a Form.Group
 *   onKeyDown   — passed through; a question's field sends on Enter
 *   placeholder, disabled, autoFocus, size  — passed through
 */
class GrowTextarea extends Component {
  _ref = React.createRef();

  componentDidMount() {
    this._grow();
  }

  componentDidUpdate() {
    this._grow();
  }

  _grow() {
    autoGrow(this._ref.current, this.props.maxRows);
  }

  handleChange = (event) => {
    const { onChange } = this.props;
    // Size on the way through: waiting for the re-render would leave the field one keystroke
    // behind its own content while somebody is typing into it.
    autoGrow(event.target, this.props.maxRows);
    if (onChange) onChange(event.target.value);
  };

  render() {
    const { value, onBlur, onKeyDown, rows = 2, placeholder, disabled, autoFocus,
            className, size = 'sm' } = this.props;
    return (
      <Form.Control
        ref={this._ref}
        as="textarea"
        rows={rows}
        size={size}
        className={className}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        // The height is autoGrow's to set, so the drag handle and the scrollbar it would fight with
        // are both off; overflow comes back the moment the content passes `maxRows`.
        style={{ resize: 'none', overflow: 'hidden' }}
        onChange={this.handleChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
      />
    );
  }
}

export default GrowTextarea;
